import { useState } from 'react';
import { useBoardStore } from '../../stores/boardStore';
import { Board, api } from '../../api/client';
import { buildBoardUrl } from '../../utils/boardRouting';

interface BoardSelectorProps {
    currentBoard: Board | null;
}

export function BoardSelector({ currentBoard }: BoardSelectorProps) {
    const { boards, switchBoard, createBoard, deleteBoard, fetchBoards, fetchBoard } = useBoardStore();
    const [isOpen, setIsOpen] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showRenameModal, setShowRenameModal] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const [selectedBoardForAction, setSelectedBoardForAction] = useState<Board | null>(null);
    const [newBoardName, setNewBoardName] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [isRenaming, setIsRenaming] = useState(false);
    const [shareLink, setShareLink] = useState('');
    const [copySuccess, setCopySuccess] = useState(false);

    const handleOpenDropdown = () => {
        fetchBoards();
        setIsOpen(true);
    };

    const handleSwitchBoard = async (id: string) => {
        setIsOpen(false);
        if (id !== currentBoard?.id) {
            await switchBoard(id);
        }
    };

    const handleCreateBoard = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newBoardName.trim()) return;

        setIsCreating(true);
        try {
            const newBoard = await createBoard(newBoardName.trim());
            setNewBoardName('');
            setShowCreateModal(false);
            await switchBoard(newBoard.id);
        } catch (error) {
            console.error('Failed to create board:', error);
        } finally {
            setIsCreating(false);
        }
    };

    const handleRenameBoard = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newBoardName.trim() || !selectedBoardForAction) return;

        setIsRenaming(true);
        try {
            await api.updateBoard(selectedBoardForAction.id, { name: newBoardName.trim() });
            setNewBoardName('');
            setShowRenameModal(false);
            setSelectedBoardForAction(null);
            fetchBoards();
            if (selectedBoardForAction.id === currentBoard?.id) {
                void fetchBoard(currentBoard.id);
            }
        } catch (error) {
            console.error('Failed to rename board:', error);
        } finally {
            setIsRenaming(false);
        }
    };

    const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

    const handleDeleteBoard = async (id: string) => {
        try {
            await deleteBoard(id);
            setConfirmDeleteId(null);
        } catch (error) {
            setConfirmDeleteId(null);
        }
    };

    const handleShareBoard = (board: Board) => {
        const link = `${window.location.origin}${buildBoardUrl(board.id)}`;
        setShareLink(link);
        setSelectedBoardForAction(board);
        setShowShareModal(true);
        setIsOpen(false);
    };

    const handleCopyLink = async () => {
        try {
            await navigator.clipboard.writeText(shareLink);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        } catch {
            // Fallback for older browsers
            const textarea = document.createElement('textarea');
            textarea.value = shareLink;
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        }
    };

    const openRenameModal = (board: Board) => {
        setSelectedBoardForAction(board);
        setNewBoardName(board.name);
        setShowRenameModal(true);
        setIsOpen(false);
    };

    return (
        <div className="relative">
            <button
                onClick={handleOpenDropdown}
                className="flex items-center gap-2 text-base sm:text-xl font-semibold text-gray-900 hover:text-blue-600 transition-colors truncate max-w-[200px] sm:max-w-[300px]"
            >
                <span className="truncate">{currentBoard?.name || 'Loading...'}</span>
                <svg className="w-4 h-4 shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            {isOpen && (
                <>
                    <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
                    <div className="absolute left-0 top-full mt-2 w-80 bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden">
                        <div className="p-2 border-b border-gray-100">
                            <div className="text-xs font-medium text-gray-400 uppercase tracking-wide px-2 py-1">
                                Your Boards
                            </div>
                        </div>

                        <div className="max-h-64 overflow-y-auto">
                            {boards.filter(b => b.is_owner).map((board) => (
                                <div
                                    key={board.id}
                                    className={`group flex items-center justify-between px-3 py-2 hover:bg-gray-50 ${board.id === currentBoard?.id ? 'bg-blue-50' : ''
                                        }`}
                                >
                                    <button
                                        onClick={() => handleSwitchBoard(board.id)}
                                        className="flex-1 text-left truncate"
                                    >
                                        <span className={`font-medium ${board.id === currentBoard?.id ? 'text-blue-600' : 'text-gray-900'}`}>
                                            {board.name}
                                        </span>
                                    </button>
                                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all">
                                        {/* Rename button */}
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                openRenameModal(board);
                                            }}
                                            className="p-1 text-gray-400 hover:text-blue-600"
                                            title="Rename"
                                        >
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                            </svg>
                                        </button>
                                        {/* Share button */}
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleShareBoard(board);
                                            }}
                                            className="p-1 text-gray-400 hover:text-green-600"
                                            title="Share"
                                        >
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                                            </svg>
                                        </button>
                                        {/* Delete button */}
                                        {boards.filter(b => b.is_owner).length > 1 && (
                                            confirmDeleteId === board.id ? (
                                                <div className="flex items-center gap-1">
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); void handleDeleteBoard(board.id); }}
                                                        className="rounded bg-red-500 px-2 py-0.5 text-[10px] font-semibold text-white"
                                                    >
                                                        Confirm
                                                    </button>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(null); }}
                                                        className="rounded bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600"
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            ) : (
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(board.id); }}
                                                    className="p-1 text-gray-400 hover:text-red-500"
                                                    title="Delete"
                                                >
                                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            )
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Shared boards */}
                        {boards.filter(b => !b.is_owner).length > 0 && (
                            <>
                                <div className="p-2 border-t border-gray-100">
                                    <div className="text-xs font-medium text-gray-400 uppercase tracking-wide px-2 py-1">
                                        Shared with you
                                    </div>
                                </div>
                                <div className="max-h-32 overflow-y-auto">
                                    {boards.filter(b => !b.is_owner).map((board) => (
                                        <button
                                            key={board.id}
                                            onClick={() => handleSwitchBoard(board.id)}
                                            className={`w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 text-left ${board.id === currentBoard?.id ? 'bg-blue-50' : ''
                                                }`}
                                        >
                                            <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                            </svg>
                                            <span className={`font-medium truncate ${board.id === currentBoard?.id ? 'text-blue-600' : 'text-gray-700'}`}>
                                                {board.name}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}

                        {/* Create new board */}
                        <div className="border-t border-gray-100 p-2">
                            <button
                                onClick={() => {
                                    setIsOpen(false);
                                    setNewBoardName('');
                                    setShowCreateModal(true);
                                }}
                                className="w-full flex items-center gap-2 px-3 py-2 text-blue-600 hover:bg-blue-50 rounded-lg font-medium"
                            >
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                                Create New Board
                            </button>
                        </div>
                    </div>
                </>
            )}

            {/* Create board modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
                        <div className="p-6">
                            <h2 className="text-xl font-semibold text-gray-900 mb-4">Create New Board</h2>
                            <form onSubmit={handleCreateBoard}>
                                <input
                                    type="text"
                                    value={newBoardName}
                                    onChange={(e) => setNewBoardName(e.target.value)}
                                    placeholder="Board name..."
                                    autoFocus
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none mb-4"
                                />
                                <div className="flex gap-2 justify-end">
                                    <button
                                        type="button"
                                        onClick={() => setShowCreateModal(false)}
                                        className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={!newBoardName.trim() || isCreating}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                    >
                                        {isCreating ? 'Creating...' : 'Create Board'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Rename board modal */}
            {showRenameModal && selectedBoardForAction && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
                        <div className="p-6">
                            <h2 className="text-xl font-semibold text-gray-900 mb-4">Rename Board</h2>
                            <form onSubmit={handleRenameBoard}>
                                <input
                                    type="text"
                                    value={newBoardName}
                                    onChange={(e) => setNewBoardName(e.target.value)}
                                    placeholder="New board name..."
                                    autoFocus
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none mb-4"
                                />
                                <div className="flex gap-2 justify-end">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowRenameModal(false);
                                            setSelectedBoardForAction(null);
                                        }}
                                        className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={!newBoardName.trim() || isRenaming}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                    >
                                        {isRenaming ? 'Saving...' : 'Save'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Share board modal */}
            {showShareModal && selectedBoardForAction && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
                        <div className="p-6">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                                    <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                                    </svg>
                                </div>
                                <div>
                                    <h2 className="text-xl font-semibold text-gray-900">Share Board</h2>
                                    <p className="text-sm text-gray-500">{selectedBoardForAction.name}</p>
                                </div>
                            </div>

                            <p className="text-gray-600 text-sm mb-4">
                                Share this link with users who have already been invited to your workspace. They'll be able to access this board.
                            </p>

                            <div className="flex gap-2 mb-4">
                                <input
                                    type="text"
                                    value={shareLink}
                                    readOnly
                                    className="flex-1 px-4 py-3 bg-gray-100 border border-gray-200 rounded-lg text-sm text-gray-700"
                                />
                                <button
                                    onClick={handleCopyLink}
                                    className={`px-4 py-3 rounded-lg font-medium transition-colors ${copySuccess
                                            ? 'bg-green-100 text-green-700'
                                            : 'bg-blue-600 text-white hover:bg-blue-700'
                                        }`}
                                >
                                    {copySuccess ? (
                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                    ) : (
                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                        </svg>
                                    )}
                                </button>
                            </div>

                            <div className="flex justify-end">
                                <button
                                    onClick={() => {
                                        setShowShareModal(false);
                                        setSelectedBoardForAction(null);
                                        setCopySuccess(false);
                                    }}
                                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                                >
                                    Done
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
